import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { useNavigate } from "react-router-dom";
import { AlertState, SiteInfo } from "./SiteAboutPageUtils/siteAboutInterfaces";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import { handleDisarmSiteFlexSchedules } from "./SiteAboutPageUtils/handleDisarmSiteFlexSchedules";
import { handleCreateSelectedSchedulesArrays } from "./SiteAboutPageUtils/handleCreateSelectedSchedulesArrays";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { NextArm, nextArmLine, useNextArm } from "./SiteAboutPageUtils/nextArm";
import { isFleetSite } from "./FlexIgnoreZonesOnboarding/flexPresetConflicts";
import { getNumbersAfterAboutSite } from "./SiteAboutPageUtils/getNumbersAfterAboutSite";

/**
 * "Next arms automatically at …" for one flex schedule, from next_arm's
 * flex_schedules row with the same id (ENG-2632). Nothing when next_arm
 * isn't available or has no row for it.
 */
export const FlexNextArmLine = ({
  nextArm,
  flexScheduleId,
}: {
  nextArm: NextArm | null;
  flexScheduleId: number | undefined;
}) => {
  const row = nextArm?.flex_schedules.find(
    (item) => item.flex_schedule_id === flexScheduleId
  );
  if (!nextArm || !row) return null;
  const text = nextArmLine(
    row.next_arm_at,
    row.supported === false ? false : row.supported ?? nextArm.supported,
    nextArm.timezone,
    nextArm.horizon_days,
    "If disarmed, it"
  );
  if (!text) return null;
  return (
    <div
      className="text-[13px] text-[#4c5257]"
      data-testid={`flex-next-arm-${flexScheduleId}`}
    >
      {text}
    </div>
  );
};

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const DisarmSiteFlexScheduleDialog = ({
  disarmSiteFlexScheduleDialogOpen,
  setDisarmSiteFlexScheduleDialogOpen,
  setSiteInfo,
  setBackdropLoadingScreenOn,
  setAlertState,
  setSyncNeeded,
  setArmedStatus,
  setArmButtonDisabled,
  setToolTipTitle,
}: {
  disarmSiteFlexScheduleDialogOpen: boolean;
  setDisarmSiteFlexScheduleDialogOpen: Dispatcher<boolean>;
  setSiteInfo: Dispatcher<SiteInfo>;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  setAlertState: Dispatcher<AlertState>;
  setSyncNeeded: Dispatcher<boolean>;
  setArmedStatus: Dispatcher<boolean>;
  setArmButtonDisabled: any;
  setToolTipTitle: any;
}) => {
  const { siteSchedules } = useSiteAboutStore();

  const navigate = useNavigate();
  // Fleet sites: next_arm can't see the fleet worker's schedules (see
  // DisarmSiteDialog), so no per-schedule line.
  const fleetSite = isFleetSite(useSiteAboutStore((state) => state.siteInfo));
  const nextArm = useNextArm(
    getNumbersAfterAboutSite(window.location.href),
    disarmSiteFlexScheduleDialogOpen && !fleetSite
  );

  // All schedules are pre-checked by default so a full site disarm stays one
  // click; the user unchecks any schedules they want to keep running.
  const [selectedValue, setSelectedValue] = React.useState({
    one: Boolean(siteSchedules[0]),
    two: Boolean(siteSchedules[1] && siteSchedules[1]?.product?.length > 0),
    three: Boolean(siteSchedules[2] && siteSchedules[2]?.product?.length > 0),
  });

  const products = JSON.parse(localStorage.products);
  const productIds = new Map();

  products.forEach((product: any) => {
    productIds.set(product.id, product.name);
  });

  return (
    <Dialog
      open={disarmSiteFlexScheduleDialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar sx={{ background: "#001943" }}>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Disarm Site
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Flex scheduling is currently enabled. Please select which schedules
          you would like to disarm. Any schedules left unselected will keep
          running and will still follow their schedule.
        </DialogContentText>
        <FormGroup className="pt-[20px]">
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedValue.one}
                onChange={(event) =>
                  setSelectedValue({
                    ...selectedValue,
                    one: event.target.checked,
                  })
                }
              />
            }
            label={
              <div>
                {`${
                  siteSchedules[0]?.display_name
                } (Active Products: ${`${siteSchedules[0]?.product
                  .map((product: number) => {
                    return `${productIds.get(product)}`;
                  })
                  .join(", ")}`})`}
                <FlexNextArmLine
                  nextArm={nextArm}
                  flexScheduleId={siteSchedules[0]?.id}
                />
              </div>
            }
          />
          {siteSchedules[1] && siteSchedules[1]?.product?.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedValue.two}
                  onChange={(event) =>
                    setSelectedValue({
                      ...selectedValue,
                      two: event.target.checked,
                    })
                  }
                />
              }
              label={
                <div>
                  {`${
                    siteSchedules[1]?.display_name
                  } (Active Products: ${`${siteSchedules[1]?.product
                    .map((product: number) => {
                      return `${productIds.get(product)}`;
                    })
                    .join(", ")}`})`}
                  <FlexNextArmLine
                    nextArm={nextArm}
                    flexScheduleId={siteSchedules[1]?.id}
                  />
                </div>
              }
            />
          )}
          {siteSchedules[2] && siteSchedules[2].product.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedValue.three}
                  onChange={(event) =>
                    setSelectedValue({
                      ...selectedValue,
                      three: event.target.checked,
                    })
                  }
                />
              }
              label={
                <div>
                  {`${
                    siteSchedules[2]?.display_name
                  } (Active Products: ${`${siteSchedules[2]?.product
                    .map((product: number) => {
                      return `${productIds.get(product)}`;
                    })
                    .join(", ")}`})`}
                  <FlexNextArmLine
                    nextArm={nextArm}
                    flexScheduleId={siteSchedules[2]?.id}
                  />
                </div>
              }
            />
          )}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <OrangeTextButton
          onClick={() => {
            setDisarmSiteFlexScheduleDialogOpen(false);
            setSiteInfo((previousState: SiteInfo) => {
              return { ...previousState, armed: true };
            });
          }}
          text="Cancel"
          disabled={false}
        />
        <OrangeTextButton
          onClick={() => {
            handleDisarmSiteFlexSchedules(
              setBackdropLoadingScreenOn,
              setSiteInfo,
              setAlertState,
              setSyncNeeded,
              setArmedStatus,
              navigate,
              setArmButtonDisabled,
              handleCreateSelectedSchedulesArrays(selectedValue, siteSchedules),
              setToolTipTitle
            );

            setSiteInfo((previousState: SiteInfo) => {
              return { ...previousState, armed: false };
            });

            setDisarmSiteFlexScheduleDialogOpen(false);
          }}
          text="Disarm Site"
          disabled={
            !selectedValue.one && !selectedValue.two && !selectedValue.three
          }
        />
      </DialogActions>
    </Dialog>
  );
};
